import { getRequest } from "lib/core/apiClient";
import { useLocale } from "lib/hooks/useLocale";
import { ExperimentInfoType } from "lib/types/experiments";
import { constants, eventTypes } from "lib/utils/constants";
import { parseExperimentResponse } from "lib/utils/experiments";
import { publishPostMessage } from "lib/utils/helpers";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Copy, LogOut } from "react-feather";
import { useSearchParams } from "react-router-dom";

interface ExperimentWidgetProps {}

const ExperimentWidget: React.FC<ExperimentWidgetProps> = () => {
  const { t } = useLocale();

  const [searchParams] = useSearchParams();
  const shopUrl = searchParams.get("shop_url");
  const merchantId = searchParams.get("mid");
  const experimentId = searchParams.get("experimentId");
  const segmentId = searchParams.get("segmentId");

  const [experimentInfo, setExperimentInfo] = useState<ExperimentInfoType | null>(null);
  const [selectedTestGroup, setSelectedTestGroup] = useState<string>(segmentId ?? "");
  const [copiedPreviewLink, setCopiedPreviewLink] = useState<boolean>(false);

  useEffect(() => {
    async function getExperimentResponse() {
      try {
        const experimentResponse = await getRequest(
          `/public/merchants/${merchantId}/experiments/${experimentId}`,
          "SEGMENTS",
        );
        if (experimentResponse?.error) {
          setExperimentInfo(null);
        } else {
          const parsedExperimentResponse = parseExperimentResponse(experimentResponse);
          setExperimentInfo(parsedExperimentResponse);
          const selectedTestGroup = localStorage.getItem(constants.EXPERIMENT_SELECTED_TEST_GROUP);
          if (selectedTestGroup) {
            setSelectedTestGroup(selectedTestGroup);
          }
          if (!selectedTestGroup) {
            setSelectedTestGroup(parsedExperimentResponse?.testGroups?.[0]?.segmentId);
          }
        }
      } catch (error) {
        setExperimentInfo(null);
      }
    }
    getExperimentResponse();
  }, []);

  useEffect(() => {
    localStorage.setItem(constants.EXPERIMENT_SELECTED_TEST_GROUP, selectedTestGroup);
    publishPostMessage(eventTypes.SET_SEGMENT_ID, {
      segmentId: selectedTestGroup,
    });
  }, [selectedTestGroup]);

  const previewLink = useMemo(() => `${shopUrl}?experimentId=${experimentId}`, [shopUrl, experimentId]);

  const handleSetExperiment = (testGroup: string) => {
    setSelectedTestGroup(testGroup);
  };

  const handleCopyPreviewLink = useCallback(() => {
    setCopiedPreviewLink(true);
    publishPostMessage(eventTypes.COPY_TEXT, {
      text: previewLink,
    });
    setTimeout(() => {
      setCopiedPreviewLink(false);
    }, 2000);
  }, [previewLink]);

  const handleClosePreview = () => {
    localStorage.removeItem(constants.EXPERIMENT_SELECTED_TEST_GROUP);
    publishPostMessage(eventTypes.CLOSE_EXPERIMENTS_PREVIEW, {});
  };

  if (!experimentInfo) {
    return (
      <div className="w-full h-full bg-carbon-dark">
        <div className="flex items-center justify-between py-5 px-[4rem]">
          <button
            className="flex items-center space-x-2 bg-white rounded-lg p-2"
            onClick={handleClosePreview}>
            <LogOut className="w-4 h-4 rotate-180" />
            <span className="text-sm font-medium">Close preview</span>
          </button>
          <span className="text-base font-medium text-white">Experiment not found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-carbon-dark">
      <div className="flex items-center justify-between py-5 px-[4rem]">
        <button
          className="flex items-center space-x-2 bg-white rounded-lg py-2 px-3"
          onClick={handleClosePreview}>
          <LogOut className="w-4 h-4 rotate-180" />
          <span className="text-sm font-medium">Close preview</span>
        </button>
        <div className="flex items-center space-x-[2.5rem]">
          {experimentInfo?.testGroups.map((testGroup) => {
            return (
              <button
                key={testGroup.segmentId}
                className={`flex items-center rounded-lg py-2 px-3 transition-all duration-300 ${
                  selectedTestGroup === testGroup.segmentId
                    ? "bg-[#01CCA7] text-white border border-[#01CCA7]"
                    : "bg-carbon-dark text-gray-dark border border-gray-dark hover:bg-[#FAFAFA1A] hover:border-[#FFFFFF40]"
                }`}
                onClick={() => handleSetExperiment(testGroup.segmentId)}>
                <span className="text-sm font-medium">{testGroup.name}</span>
              </button>
            );
          })}
        </div>
        <button
          className="flex items-center space-x-2 bg-white rounded-lg py-2 px-3"
          onClick={handleCopyPreviewLink}>
          <span className="text-sm font-medium">Copy preview link</span>
          {copiedPreviewLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export default ExperimentWidget;
